import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { typography } from '@icebreaker/shared/design';
import {
  STAFF_LIMITS,
  STAFF_PASSWORD_POLICY,
  checkStaffPasswordPolicy,
} from '@icebreaker/shared/staff';
import {
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  PageShell,
  PasswordRuleList,
  fieldStyles,
  useFormErrors,
} from '@icebreaker/ui';
import { ROUTES } from '@/app/routes';
import { staffSetup } from '@/features/auth/store/auth.slice';
import { selectAuth, selectSetupState } from '@/features/auth/store/auth.selectors';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import { BrandLock } from '@/shared/ui';
import styles from './staff-access.module.css';

/**
 * İLK KURULUM — DAVET JETONUYLA ŞİFRE BELİRLEME (Karar 4c).
 *
 * PANELDE "ŞİFREMİ UNUTTUM" YOKTUR ve bu ekran onun yerine geçmez: jeton
 * kullanıcıdan değil, BİR YÖNETİCİNİN gönderdiği e-postadan gelir. Yani
 * kurtarma iki şey ister — posta kutusu VE bir insan kararı. Ölçülmüş P1'in
 * zinciri ("posta kutusu → panel") bu yüzden burada yeniden kurulmuyor.
 *
 * JETON ADRES ÇUBUĞUNDAN OKUNUR (`?token=` — sunucunun ürettiği ad; eski
 * `?jeton=` ikinci sırada okunur) ama alan DÜZENLENEBİLİR kalır:
 * e-posta istemcileri uzun bağlantıları kırpar ve personel jetonu elle
 * yapıştırmak zorunda kalabilir. Alan boşken düğme kapalıdır.
 *
 * BAŞARI OTURUM AÇAR: sunucu davetle birlikte oturumu da verir, personel
 * yeni belirlediği şifreyle kod adımına geri düşmez.
 */
export function SetupScreen() {
  const t = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [params] = useSearchParams();
  const auth = useAppSelector(selectAuth);
  const setupState = useAppSelector(selectSetupState);
  const formErrors = useFormErrors(setupState.error, INLINE_ERROR_PATHS);

  const [token, setToken] = useState(
    params.get(TOKEN_PARAM) ?? params.get(LEGACY_TOKEN_PARAM) ?? '',
  );
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');

  useEffect(() => {
    if (auth.isAuthenticated) void navigate(ROUTES.home, { replace: true });
  }, [auth.isAuthenticated, navigate]);

  const mismatch = repeat.length > 0 && repeat !== password;
  const canSubmit =
    token.trim().length > 0 &&
    checkStaffPasswordPolicy(password).ok &&
    repeat.length > 0 &&
    !mismatch &&
    setupState.status !== 'loading';

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    dispatch(staffSetup.request({ token: token.trim(), newPassword: password }));
  };

  return (
    <PageShell rail="reading" as="main" className={styles.page}>
      <BrandLock square={40} wordmark={typography.fontSize.xl} className={styles.brand} />
      <Card title={t.t('admin.panel.setup.title')} className={styles.card}>
        <p className={styles.body}>{t.t('admin.panel.setup.body')}</p>
        <form onSubmit={submit} className={styles.form}>
          <Field
            id="staff-setup-token"
            label={t.t('admin.panel.setup.tokenLabel')}
            hint={t.t('admin.panel.setup.tokenHint')}
            error={formErrors.at('token')}
          >
            <Input
              id="staff-setup-token"
              name="token"
              autoComplete="off"
              value={token}
              onChange={(event) => setToken(event.target.value)}
              invalid={Boolean(formErrors.at('token'))}
              required
            />
          </Field>
          <Field
            id="staff-setup-password"
            label={t.t('admin.panel.password.newLabel')}
            hint={t.t('admin.panel.password.hint')}
            error={formErrors.at('newPassword')}
          >
            <Input
              id="staff-setup-password"
              name="newPassword"
              type="password"
              autoComplete="new-password"
              minLength={STAFF_LIMITS.passwordMinLength}
              maxLength={STAFF_LIMITS.passwordMaxLength}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              invalid={Boolean(formErrors.at('newPassword'))}
              aria-describedby="staff-setup-password-rules"
              required
            />
            <PasswordRuleList
              id="staff-setup-password-rules"
              password={password}
              policy={STAFF_PASSWORD_POLICY}
            />
          </Field>
          <Field
            id="staff-setup-repeat"
            label={t.t('admin.panel.password.repeatLabel')}
            error={mismatch ? t.t('admin.panel.password.repeatMismatch') : undefined}
          >
            <Input
              id="staff-setup-repeat"
              name="repeatPassword"
              type="password"
              autoComplete="new-password"
              value={repeat}
              onChange={(event) => setRepeat(event.target.value)}
              invalid={mismatch}
              required
            />
          </Field>
          {/*
            ÖZET CÜMLELER ÇEVRİLMİŞTİR, DİKİŞE VERİLMEZ (error-message.guard KURAL 5b).
            `ErrorText` çeviriyi kendi yapar; `formErrors.summary` ise hazır
            cümledir. Cümle `ErrorText`e verilince dikiş onu KOD sanıyor ve her
            hatayı "Beklenmeyen bir hata oluştu" diye yazıyordu — iptal edilen
            davet bağlantısında gerçek tarayıcıda ölçüldü (sunucu
            `STAFF_INVITATION_INVALID` döndü, ekran jenerik cümle bastı).
          */}
          {formErrors.summary.map((sentence) => (
            <p key={sentence} className={fieldStyles.error} role="alert">
              {sentence}
            </p>
          ))}
          <div className={styles.actions}>
            <Button type="submit" variant="primary" disabled={!canSubmit}>
              {t.t('admin.panel.setup.submit')}
            </Button>
            <Button type="button" variant="ghost" onClick={() => void navigate(ROUTES.signIn)}>
              {t.t('admin.panel.setup.backToSignIn')}
            </Button>
          </div>
        </form>
      </Card>
      {/*
        BOŞ DURUM: jeton hiç verilmemişse (doğrudan adres yazılmış) personel
        ne yapacağını bilmeli. Uydurma bir "davet iste" düğmesi YOKTUR —
        panelde self-servis kurtarma yoktur; tek yol bir yöneticiye ulaşmaktır.
      */}
      {token.trim().length === 0 ? (
        <EmptyState
          title={t.t('admin.panel.setup.emptyTitle')}
          body={t.t('admin.panel.setup.emptyBody')}
        />
      ) : null}
      <p className={styles.note}>{t.t('admin.panel.signIn.restricted')}</p>
    </PageShell>
  );
}

/**
 * DAVET BAĞLANTISININ SORGU PARAMETRESİ — SUNUCUNUN ÜRETTİĞİ AD.
 *
 * `StaffAccountsService.issueInvitation` bağlantıyı `/kurulum?token=…` diye
 * yazar. Ekran S2'de `jeton` okuyordu; e-postadaki bağlantı alanı BOŞ açıyor
 * ve personel kodu elle yapıştırmak zorunda kalıyordu (panel personel
 * ekranları turu, ana giriş yolundan ölçüldü — md. 1b). Eski ad ikinci
 * sırada okunmaya devam eder: elle paylaşılmış bir bağlantı kırılmasın.
 */
const TOKEN_PARAM = 'token';
const LEGACY_TOKEN_PARAM = 'jeton';

/** Cümlesi ALANIN ALTINA yazılan sunucu yolları. */
const INLINE_ERROR_PATHS = ['token', 'newPassword'];
